import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import appConfig from "../config";

function MessagesHistory() {
  const [data, setData] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState("");
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // Načítaj zoznam chatbotov na filter
  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${appConfig.BASE_URL}/api/chatbots/messages-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const bots = await res.json();
        setChatbots(bots);
      } catch (err) {
        setChatbots([]);
      }
    };
    fetchChatbots();
  }, []);

  // Načítaj správy podľa filtra a stránkovania
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        let url = `${appConfig.BASE_URL}/api/chatbots/messages`;
        const params = [];
        if (selectedChatbot) params.push(`chatbot_id=${selectedChatbot}`);
        if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
        params.push(`page=${page}`);
        params.push(`limit=${limit}`);
        if (params.length) url += `?${params.join("&")}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const messages = await res.json();
        setData({
          columns: [
            { Header: "ID", accessor: "id", align: "left" },
            { Header: "Chatbot ID", accessor: "chatbot_id", align: "left" },
            { Header: "Role", accessor: "role", align: "left" },
            { Header: "Content", accessor: "content", align: "left" },
            { Header: "Created At", accessor: "created_at", align: "left" },
          ],
          rows: Array.isArray(messages.rows) ? messages.rows : [],
        });
        setTotal(messages.total || 0);
      } catch (err) {
        setError("Error loading messages");
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [selectedChatbot, searchQuery, page, limit]);

  // Reset page na 1 pri zmene filtra/search
  useEffect(() => {
    setPage(1);
  }, [selectedChatbot, searchQuery]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                mb: 3,
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
                borderRadius: 3,
                background: "#f8fafc",
              }}
            >
              <MDTypography variant="h5" color="text" sx={{ minWidth: 180 }}>
                Message History
              </MDTypography>
              <TextField
                select
                label="Chatbot"
                value={selectedChatbot}
                onChange={(e) => setSelectedChatbot(e.target.value)}
                size="medium"
                sx={{
                  minWidth: 240,
                  maxWidth: 320,
                  borderRadius: 2,
                  background: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  ".MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 16,
                    height: 48,
                  },
                  ".MuiSelect-select": {
                    padding: "14px 20px",
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                {chatbots.map((bot) => (
                  <MenuItem key={bot.chatbot_id} value={bot.chatbot_id}>
                    {bot.chatbot_id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="medium"
                sx={{
                  minWidth: 400,
                  maxWidth: 700,
                  borderRadius: 2,
                  background: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  ".MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 16,
                    height: 48,
                  },
                  input: {
                    padding: "14px 20px",
                  },
                }}
                placeholder="Enter the message text you are looking for..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") setSearchQuery(search);
                }}
              />
              <Button
                variant="contained"
                color="info"
                size="large"
                sx={{
                  height: 48,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 16,
                  px: 4,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
                onClick={() => setSearchQuery(search)}
              >
                Search
              </Button>
              <TextField
                select
                label="Per page"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                size="medium"
                sx={{
                  minWidth: 120,
                  borderRadius: 2,
                  background: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  ".MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 16,
                    height: 48,
                  },
                }}
                style={{ marginLeft: 16 }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Paper>
            <Card>
              <MDBox pt={3}>
                {loading ? (
                  <MDTypography variant="button" color="info">
                    Loading messages...
                  </MDTypography>
                ) : error ? (
                  <MDTypography variant="button" color="error">
                    {error}
                  </MDTypography>
                ) : (
                  <>
                    <DataTable
                      table={data}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      pagination={false}
                      noEndBorder
                    />
                    <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
                      <Pagination
                        count={Math.max(1, Math.ceil(total / limit))}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="info"
                        shape="rounded"
                        size="large"
                        showFirstButton
                        showLastButton
                        sx={{
                          ".MuiPagination-ul": { justifyContent: "center" },
                          my: 2,
                        }}
                      />
                    </Stack>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MessagesHistory;
